import React from 'react';
import { Clock, ChevronRight, CircleX } from 'lucide-react';
import { cn } from '../lib/utils';
import { useTranslation } from 'react-i18next';

interface Action {
  title: string;
  description: string;
  dueDate: string;
  priority: 'high' | 'medium' | 'low';
}

interface UpcomingActionsProps {
  actions: Action[];
}

export const UpcomingActions: React.FC<UpcomingActionsProps> = ({ actions }) => {
  const { t } = useTranslation();
  
  return (
    <div className="space-y-2 min-h-[100px] flex flex-col justify-between">
      {actions.map((action, index) => (
        <div key={index} className="border rounded-lg p-3 hover:bg-gray-50 hover-scale group cursor-pointer">
          <div className="flex items-start justify-between mb-1">
            <div>
              <h4 className="font-medium text-gray-900 mb-1">{action.title}</h4>
              <p className="text-xs text-gray-600 mb-2">{action.description}</p>
            </div>
            <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-hana-green transition-colors" />
          </div>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center text-xs text-gray-500">
              <Clock className="w-3.5 h-3.5 mr-1" />
              {t('dashboard.upcomingActions.dueIn', 'Due in')} {action.dueDate}
            </div>
            <span 
              className={cn(
                "inline-flex px-2 py-0.5 rounded-full text-xs font-medium",
                action.priority === 'high' ? "bg-red-100 text-red-800" :
                action.priority === 'medium' ? "bg-amber-100 text-amber-800" :
                "bg-blue-100 text-blue-800"
              )}
            >
              {action.priority === 'high' ? t('dashboard.priority.high', 'High') : 
               action.priority === 'medium' ? t('dashboard.priority.medium', 'Medium') : 
               t('dashboard.priority.low', 'Low')} {t('dashboard.priority.priority', 'Priority')}
            </span>
          </div>
        </div>
      ))}
      {actions.length === 0 && (
        <div className="flex items-center justify-center mt-4 bg-gray-50 p-4 rounded-lg border border-gray-200">
          <div className="text-gray-500 flex items-center justify-center">
            <CircleX className="w-4 h-4 mr-2" />
            <p className="text-sm">{t('dashboard.upcomingActions.noActions', 'No upcoming actions')}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default UpcomingActions;